import { User } from "configcat-js";
import { makeAutoObservable, runInAction } from "mobx";

import { ConfigCatKeys, getConfigCatValue } from "../../common/configCatClient";
import { logger } from "../../common/logger";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { Company } from "../../common/types/companyTypes";
import type { CustomerFeature, FEATURE_NAME } from "../../common/types/featureTypes";
import { keepIdOnly } from "../../common/utils/segmentUtils";
import { getNavigationTestVariantFromStorage, setNavigationTestVariantInStorage } from "../../common/utils/storage/storageUtils";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";

type FeatureStoreType = BaseStoreType & {
   customerFeatures: FEATURE_NAME[];
   barcodeScannerEnabled: boolean;
   claimsEnabled: boolean;
   pickupOrderAvailable: boolean;
   notificationsEnabled: boolean;
   newPromotionNotificationsEnabled: boolean;
   keyFiguresAvailable: boolean;
   recommendationType: "AI1" | "AI2" | "AI3" | "OFF";
   navigationTestVariant: "megamenu" | "sidemenu" | "topmenu";
   contractualRecommendations: boolean;
   voucherifyAvailable: boolean;
   useDynamicsForNotificationProfiles: boolean;
   prioritizeAgreementAssortment: boolean;
   searchResultsSortedByCustomerGroupSales: boolean;
   apiProxyOrders: boolean;
   apiProxyInvoices: boolean;
   apiProxyFeatures: boolean;
   apiProxyDeliveryWindows: boolean;
   apiProxyNotificationProfiles: boolean;
   filterDisplayThreshold: number;

   fetchEnabledConfigCatFeatures: (company?: Company) => Promise<void>;
   fetchEnabledCustomerFeatures: (customerNumber: string) => Promise<void>;
   hasCustomerFeature: (feature: FEATURE_NAME) => boolean;
};

export const featuresStore: FeatureStoreType = makeAutoObservable({
   customerFeatures: [],
   barcodeScannerEnabled: false,
   claimsEnabled: false,
   pickupOrderAvailable: false,
   notificationsEnabled: false,
   newPromotionNotificationsEnabled: false,
   keyFiguresAvailable: false,
   recommendationType: "AI1",
   contractualRecommendations: false,
   voucherifyAvailable: false,
   useDynamicsForNotificationProfiles: false,
   prioritizeAgreementAssortment: false,
   searchResultsSortedByCustomerGroupSales: false,
   apiProxyOrders: false,
   apiProxyInvoices: false,
   apiProxyFeatures: false,
   apiProxyDeliveryWindows: false,
   apiProxyNotificationProfiles: false,
   filterDisplayThreshold: 8,

   navigationTestVariant: "megamenu",

   fetchEnabledConfigCatFeatures: async (company) => {
      const userData = new User(company?.customerNumber || "Anonymous");

      userData.custom = {
         chainLevel1: keepIdOnly(company?.businessLevels.level1 || "N/A"),
         chainLevel2: keepIdOnly(company?.businessLevels.level2 || "N/A"),
         chainLevel3: keepIdOnly(company?.businessLevels.level3 || "N/A"),
         chainLevel4: keepIdOnly(company?.businessLevels.level4 || "N/A"),
         customerGroup: keepIdOnly(company?.customerGroup || "N/A")
      };

      const flagsAndValues: Partial<FeatureStoreType> = {};

      flagsAndValues.barcodeScannerEnabled = await getConfigCatValue(ConfigCatKeys.BARCODESCANNER_AVAILABLE, userData);
      flagsAndValues.claimsEnabled = await getConfigCatValue(ConfigCatKeys.CLAIMS_AVAILABLE, userData);
      flagsAndValues.pickupOrderAvailable = await getConfigCatValue(ConfigCatKeys.PICKUP_ORDER_AVAILABLE, userData);
      flagsAndValues.notificationsEnabled = await getConfigCatValue(ConfigCatKeys.NOTIFICATIONS_AVAILABLE, userData);
      flagsAndValues.newPromotionNotificationsEnabled = await getConfigCatValue(
         ConfigCatKeys.NEW_PROMOTION_NOTIFICATIONS_AVAILABLE,
         userData
      );
      flagsAndValues.recommendationType = await getConfigCatValue(ConfigCatKeys.RECOMMENDATION_TYPE, userData);
      flagsAndValues.keyFiguresAvailable = await getConfigCatValue(ConfigCatKeys.KEYFIGURES_AVAILABLE, userData);
      flagsAndValues.contractualRecommendations = await getConfigCatValue(ConfigCatKeys.CONTRACTUAL_RECOMMENDATIONS, userData);
      flagsAndValues.voucherifyAvailable = await getConfigCatValue(ConfigCatKeys.VOUCHERIFY_AVAILABLE, userData);
      flagsAndValues.useDynamicsForNotificationProfiles = await getConfigCatValue(
         ConfigCatKeys.USE_DYNAMICS_FOR_NOTIFICATION_PROFILES,
         userData
      );
      flagsAndValues.prioritizeAgreementAssortment = await getConfigCatValue(
         ConfigCatKeys.PRIORITIZE_AGREEMENT_ASSORTMENT,
         userData
      );
      flagsAndValues.searchResultsSortedByCustomerGroupSales = await getConfigCatValue(
         ConfigCatKeys.SEARCH_RESULTS_SORTED_BY_CUSTOMER_GROUP_SALES,
         userData
      );
      flagsAndValues.filterDisplayThreshold = await getConfigCatValue(ConfigCatKeys.FILTER_DISPLAY_THRESHOLD, userData);
      if (userData.custom.customerNumber !== "Anonymous") {
         flagsAndValues.navigationTestVariant = await getConfigCatValue(ConfigCatKeys.NAVIGATION_TEST_VARIANT, userData);
         setNavigationTestVariantInStorage(featuresStore.navigationTestVariant);
      } else {
         flagsAndValues.navigationTestVariant = getNavigationTestVariantFromStorage() || "megamenu";
      }
      flagsAndValues.apiProxyOrders = await getConfigCatValue(ConfigCatKeys.API_PROXY_ORDERS, userData);
      flagsAndValues.apiProxyInvoices = await getConfigCatValue(ConfigCatKeys.API_PROXY_INVOICES, userData);
      flagsAndValues.apiProxyFeatures = await getConfigCatValue(ConfigCatKeys.API_PROXY_FEATURES, userData);
      flagsAndValues.apiProxyDeliveryWindows = await getConfigCatValue(ConfigCatKeys.API_PROXY_DELIVERY_WINDOWS, userData);
      flagsAndValues.apiProxyNotificationProfiles = await getConfigCatValue(
         ConfigCatKeys.API_PROXY_NOTIFICATION_PROFILES,
         userData
      );
      runInAction(() => {
         Object.assign(featuresStore, flagsAndValues);
      });
   },

   fetchEnabledCustomerFeatures: async (customerNumber) => {
      const resp: CustomerFeature[] = await theme.fetchCustomerFeatures(customerNumber, authStore.getSessionToken() || "");
      runInAction(() => {
         featuresStore.customerFeatures = resp.map((feature) => feature.name);
      });

      logger.log("auth", "Features found: ", featuresStore.customerFeatures);
   },

   hasCustomerFeature: (featureName) => {
      return featuresStore.customerFeatures.includes(featureName);
   },
   clearCompanySpecificData: () => {
      featuresStore.customerFeatures = [];
      void featuresStore.fetchEnabledConfigCatFeatures();
   }
} satisfies FeatureStoreType);

export default featuresStore;
